import { Injectable, inject } from '@angular/core';
import { UserCapability } from 'identity-management';
import { UserCapabilityGroup } from '../models';
import { AppRoutes, SidebarRoutes } from 'shared-utils';
import { translate } from 'translations-utils';
import { UserService } from 'shared-data-access';

@Injectable({
  providedIn: 'root',
})
export class CapabilitiesService {
  private userService = inject(UserService);

  readonly capabilityToGroup: {
    [key in UserCapability]: { label: string; group: UserCapabilityGroup };
  } = {
    [UserCapability.ContractCreation]: {
      label: translate('portal.capability.base.create'),
      group: UserCapabilityGroup.Contracts,
    },
    [UserCapability.ContractModification]: {
      label: translate('portal.capability.base.modify'),
      group: UserCapabilityGroup.Contracts,
    },
    [UserCapability.ContractView]: {
      label: translate('portal.capability.base.view'),
      group: UserCapabilityGroup.Contracts,
    },
    [UserCapability.ContractSelfUnderwriting]: {
      label: translate('portal.capability.contracts.self-underwriting'),
      group: UserCapabilityGroup.Contracts,
    },
    [UserCapability.PowerbiReports]: {
      group: UserCapabilityGroup.Contracts,
      label: translate('portal.capability.powerbi.reports'),
    },
    [UserCapability.PersonCreation]: {
      label: translate('portal.capability.base.create'),
      group: UserCapabilityGroup.Policyholders,
    },
    [UserCapability.PersonModification]: {
      label: translate('portal.capability.base.modify'),
      group: UserCapabilityGroup.Policyholders,
    },
    [UserCapability.PersonView]: {
      label: translate('portal.capability.base.view'),
      group: UserCapabilityGroup.Policyholders,
    },
    [UserCapability.TenantCreation]: {
      label: translate('portal.capability.base.create'),
      group: UserCapabilityGroup.Tenants,
    },
    [UserCapability.TenantModification]: {
      label: translate('portal.capability.base.modify'),
      group: UserCapabilityGroup.Tenants,
    },
    [UserCapability.TenantView]: {
      label: translate('portal.capability.base.view'),
      group: UserCapabilityGroup.Tenants,
    },
    [UserCapability.TenantRemoval]: {
      label: translate('portal.capability.base.remove'),
      group: UserCapabilityGroup.Tenants,
    },
    [UserCapability.ExternalUserCreation]: {
      label: translate('portal.capability.base.create'),
      group: UserCapabilityGroup.ExternalUsers,
    },
    [UserCapability.ExternalUserModification]: {
      label: translate('portal.capability.base.modify'),
      group: UserCapabilityGroup.ExternalUsers,
    },
    [UserCapability.ExternalUserView]: {
      label: translate('portal.capability.base.view'),
      group: UserCapabilityGroup.ExternalUsers,
    },
    [UserCapability.InternalUserCreation]: {
      label: translate('portal.capability.base.create'),
      group: UserCapabilityGroup.InternalUsers,
    },
    [UserCapability.InternalUserModification]: {
      label: translate('portal.capability.base.modify'),
      group: UserCapabilityGroup.InternalUsers,
    },
    [UserCapability.InternalUserView]: {
      label: translate('portal.capability.base.view'),
      group: UserCapabilityGroup.InternalUsers,
    },
    [UserCapability.LinkCreation]: {
      label: translate('portal.capability.base.create'),
      group: UserCapabilityGroup.LinkBuilder,
    },
    [UserCapability.LinkModification]: {
      label: translate('portal.capability.base.modify'),
      group: UserCapabilityGroup.LinkBuilder,
    },
    [UserCapability.LinkView]: {
      label: translate('portal.capability.base.view'),
      group: UserCapabilityGroup.LinkBuilder,
    },
    [UserCapability.ProviderSettingsCreation]: {
      label: translate('portal.capability.base.create'),
      group: UserCapabilityGroup.ProviderSettings,
    },
    [UserCapability.ProviderSettingsModification]: {
      label: translate('portal.capability.base.modify'),
      group: UserCapabilityGroup.ProviderSettings,
    },
    [UserCapability.ProviderSettingsView]: {
      label: translate('portal.capability.base.view'),
      group: UserCapabilityGroup.ProviderSettings,
    },
    [UserCapability.ProviderSettingsRemoval]: {
      label: translate('portal.capability.base.remove'),
      group: UserCapabilityGroup.ProviderSettings,
    },
    [UserCapability.AdministrativeNotifications]: {
      label: translate('portal.capability.base.notifications'),
      group: UserCapabilityGroup.Administrative,
    },
  };

  readonly capabilityGroupNameToLabel: { [key in UserCapabilityGroup]: string } = {
    [UserCapabilityGroup.Contracts]: translate('portal.capability.contracts'),
    [UserCapabilityGroup.Policyholders]: translate('portal.capability.policyholders'),
    [UserCapabilityGroup.Tenants]: translate('portal.capability.tenants'),
    [UserCapabilityGroup.ExternalUsers]: translate('portal.capability.external-users'),
    [UserCapabilityGroup.InternalUsers]: translate('portal.capability.internal-users'),
    [UserCapabilityGroup.LinkBuilder]: translate('portal.capability.link-builder'),
    [UserCapabilityGroup.ProviderSettings]: translate('portal.capability.provider-settings'),
    [UserCapabilityGroup.Administrative]: translate('portal.capability.administrative'),
  };

  readonly capabilityGroupNameToDescription: { [key in UserCapabilityGroup]: string } = {
    [UserCapabilityGroup.Contracts]: translate('portal.capability.contracts.description'),
    [UserCapabilityGroup.Policyholders]: translate('portal.capability.policyholders.description'),
    [UserCapabilityGroup.Tenants]: translate('portal.capability.tenants.description'),
    [UserCapabilityGroup.ExternalUsers]: translate('portal.capability.external-users.description'),
    [UserCapabilityGroup.InternalUsers]: translate('portal.capability.internal-users.description'),
    [UserCapabilityGroup.LinkBuilder]: translate('portal.capability.link-builder.description'),
    [UserCapabilityGroup.ProviderSettings]: '',
    [UserCapabilityGroup.Administrative]: '',
  };

  private readonly routeCapabilityMap: {
    [key in SidebarRoutes]: UserCapability[];
  } = {
    [AppRoutes.Contracts]: [UserCapability.ContractView],
    [AppRoutes.Products]: [UserCapability.ContractCreation],
    [AppRoutes.Policyholders]: [UserCapability.PersonView],
    [AppRoutes.Tenants]: [UserCapability.TenantView, UserCapability.TenantCreation, UserCapability.TenantModification],
    [AppRoutes.Users]: [
      UserCapability.ExternalUserView,
      UserCapability.ExternalUserCreation,
      UserCapability.ExternalUserModification,
    ],
    [AppRoutes.InternalUsers]: [
      UserCapability.InternalUserView,
      UserCapability.InternalUserCreation,
      UserCapability.InternalUserModification,
    ],
    [AppRoutes.LinkBuilder]: [UserCapability.LinkView, UserCapability.LinkCreation, UserCapability.LinkModification],
    [AppRoutes.RenewalReport]: [UserCapability.PowerbiReports],
    [AppRoutes.SalesReport]: [],
    [AppRoutes.ProviderSettings]: [
      UserCapability.ProviderSettingsView,
      UserCapability.ProviderSettingsCreation,
      UserCapability.ProviderSettingsModification,
      UserCapability.ProviderSettingsRemoval,
    ],
  };

  getCapabilitiesByGroup(group: UserCapabilityGroup): UserCapability[] {
    return (Object.keys(this.capabilityToGroup) as UserCapability[]).filter(
      (key: UserCapability) => this.capabilityToGroup[key].group === group
    );
  }

  getCapabilityGroupLabel(groupName: UserCapabilityGroup): string {
    return this.capabilityGroupNameToLabel[groupName];
  }

  getCapabilityGroupDescription(groupName: UserCapabilityGroup): string {
    return this.capabilityGroupNameToDescription[groupName];
  }

  getCapabilityGroups(): UserCapabilityGroup[] {
    return Object.values(UserCapabilityGroup) as UserCapabilityGroup[];
  }

  hasCapability(capability: UserCapability): boolean {
    return this.userService.hasCapability(capability);
  }

  hasEveryCapability(capabilities: UserCapability[]): boolean {
    return !capabilities.length || capabilities.every((capability: UserCapability) => this.hasCapability(capability));
  }

  hasSomeCapability(capabilities: UserCapability[]): boolean {
    return !capabilities.length || capabilities.some((capability: UserCapability) => this.hasCapability(capability));
  }

  getCapabilitiesForRoute(route: SidebarRoutes): UserCapability[] {
    return this.routeCapabilityMap[route];
  }
}
